import { compact, isNaN } from "lodash";

import {
	ModelEventAction,
	ModelEventConstructor,
} from "../../utils/ModelEventConstructor";
import SubscriptionPool from "../../redux/services/SubscriptionPool";
import { DiscountPlan } from "../../redux/services/Order/getPhoneInfo";
import { getPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";
import createLogger from "../../utils/logger.util";
import { Language } from "../../assets/languages/langs";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { PaymentAccount } from "../../types/PaymentAccount";
import Gender from "../../types/Gender";
import Nullable from "../../types/Nullable";
import { Base, Person, File, ClientGroup, CustomerRate } from "..";

import { destroyOne } from "./utils";

const logger = createLogger({ name: "Client" });

class Client extends Base {
	static defaultSharedOptions: Client.SharedOptions = {
		deprecate: true,
	};

	static fromResponse(data: any): Client.Model {
		const paymentAccounts = data?.paymentAccounts?.filter?.((item) => {
			if (item?.type === "main" || item?.type === "bonus") {
				return true;
			}
			return false;
		});

		const plan = (value, type: "additionalPlan" | "mainPlan") => {
			try {
				const exist = value?.[type];
				if (!exist) return undefined;
				return {
					id: exist?.id,
					active: exist?.active,
					name: exist?.name,
					discount:
						exist?.configuration?.types?.[exist.configuration?.type]
							?.discount,
					maxDiscount: exist?.configuration?.general?.maxDiscount,
					type: exist?.configuration?.type,
					types: exist?.configuration?.types,
				};
			} catch {
				return undefined;
			}
		};

		return {
			id: data.id,

			additionalPlan: plan(data, "additionalPlan"),
			mainPlan: plan(data, "mainPlan"),

			mainRate: data?.mainPlan
				? CustomerRate.fromResponse(data.mainPlan)
				: undefined,
			additionalRate: data?.additionalPlan
				? CustomerRate.fromResponse(data.additionalPlan)
				: undefined,

			rideCount: data?.rideCount,
			counters: data?.counters,

			companyId:
				data?.defaultTaxiService?.company?.id || data.company?.id,
			carClassId: data?.carClass?.id,
			defaultTaxiServiceId: data?.defaultTaxiService?.id,
			latestTaxiServiceId: data?.latestTaxiService?.id,

			person: data.person
				? {
						id: data.person?.id,

						firstName: data?.person?.name,
						lastName: data?.person?.surname,
						fatherName: data?.person?.fatherName,
						birthday: data?.person?.birthday,

						emails: data?.person?.emails,
						phones: data?.person?.phones,

						createdAt: data?.person?.createdAt,
						updatedAt: data?.person?.updatedAt,
						deletedAt: data?.person?.deletedAt,
				  }
				: data?.person,

			login: data?.login,
			hasPassword: data?.isPassword,
			gender: data?.gender,
			status: data?.status,
			notes: data?.notes || "",
			orderNotes: data?.orderNotes || "",
			executorNotes: data?.executorNotes || "",

			smsNotifications: data?.smsNotifications,

			additionalFields: {
				defaultAddress: data?.additionalFields?.defaultAddress,

				...data?.additionalFields,
			},
			paymentAccounts: compact(paymentAccounts) ?? [],

			customerToForbiddenExecutors: data?.customerToForbiddenExecutors,
			customerToUndesirableExecutors:
				data?.customerToUndesirableExecutors,
			customerToPriorityExecutors: data?.customerToPriorityExecutors,

			personalFiles: data?.personalFiles?.map(File.fromResponse),
			otherFiles: data?.otherFiles?.map(File.fromResponse),
			downloadedFiles: data?.downloadedFiles?.map(File.fromResponse),

			customerGroup: data?.customerGroup,
			customerGroupId: data?.customerGroup?.id,

			passengers: data?.passengers,
			customerToPaymentTypes: data?.customerToPaymentTypes,
			customerToCustomerCompanies: data?.customerToCustomerCompanies,

			isApp: data?.isApp,
			isAllowedApp: data?.isAllowedApp,
			isAllowedCabinet: data?.isAllowedCabinet,

			createdAt: data?.createdAt,
			updatedAt: data?.updatedAt,
			deletedAt: data?.deletedAt,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	static toRequest(
		model: (Client.Model.New & { id?: undefined }) | Client.Model.Modified,
	): any {
		const mainDiscountPlanId =
			model.mainRateId === 0 || isNaN(model.mainRateId)
				? null
				: model.mainRateId;
		const additionalDiscountPlanId =
			model.additionalRateId === 0 || isNaN(model.additionalRateId)
				? null
				: model.additionalRateId;

		const params: Client.Model.CreateParams = {
			customerGroupId: model.customerGroupId,

			companyId: model.id == null ? model.companyId : undefined,
			carClassId: model.carClassId,
			defaultTaxiServiceId: model.defaultTaxiServiceId,

			phones:
				model.person?.phones?.filter(Boolean).map((number, i) => ({
					number,
					group: i === 0 ? 0 : 1,
				})) || [],
			emails:
				model.person?.emails?.filter(Boolean).map((value) => ({
					value,
					group: 0,
				})) || [],

			name: model.person?.firstName || "",
			surname: model.person?.lastName || "",
			fatherName: model.person?.fatherName || "",
			birthday:
				model.person?.birthday == null
					? undefined
					: new Date(model.person?.birthday),
			city: model.person?.city || undefined,
			country: model.person?.country || undefined,
			address: model.person?.address || undefined,

			login: model.login,
			password: model?.password,
			gender: model.gender,
			status: model.status,

			notes: model.notes,
			orderNotes: model?.orderNotes || "",
			executorNotes: model?.executorNotes || "",

			// avatarFileId?: model.		,
			// otherFileIds?: model.		,

			forbiddenExecutors: model.customerToForbiddenExecutors,
			undesirableExecutors: model.customerToUndesirableExecutors,
			priorityExecutors: model.customerToPriorityExecutors,

			smsNotifications: model.smsNotifications,

			isAllowedApp: model.isAllowedApp,
			isAllowedCabinet: model.isAllowedCabinet,

			additionalFields: model.additionalFields,

			personalFileIds: model.personalFileIds,
			otherFileIds: model.otherFileIds,
			// downloadedFileIds: model.downloadedFileIds,
		};

		if (!("id" in model)) {
			if (model.paymentTransactions.main.length)
				params.paymentTransactions = {
					main: model.paymentTransactions.main,
				};
			if (model.paymentTransactions.bonus.length)
				params.paymentTransactions = {
					...params.paymentTransactions,
					bonus: model.paymentTransactions.bonus,
				};
		}

		logger.info("Client toRequest", {
			model,
			params,
			mainDiscountPlanId,
			additionalDiscountPlanId,
		});

		return {
			...params,
			mainDiscountPlanId,
			additionalDiscountPlanId,
		};
	}

	public static async getById(
		id: number,
	): Promise<Client.Model | undefined | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.customer.getById(id),
				{ silent: false, error: true },
			);

			logger.info("[Client] getById", res);
			if (!res?.id) return null;

			return this.fromResponse(res);
		} catch (error) {
			logger.error("[Client] error getById", error);
			return null;
		}
	}

	public static async getAll(
		options: Client.SubscribeOptions & {
			fromDB?: boolean;
			/** Вернуть общее число записей под фильтрами (для экспорта). */
			withTotal?: boolean;
		},
	): Promise<{
		items: Client.Model[];
		pagination: {
			limit: number;
			offset: number;
			count: number;
		};
		total?: number;
	}> {
		try {
			const params = {
				query: options.query,
				offset: options.offset,
				limit: options.limit,
				order: options.order,
				subscribeType: options.subscribeType,
				lang: options.language,
				defaultTaxiServiceIds: options.defaultTaxiServiceIds,
				companyIds: options.companyIds,
				isApp: options.isApp,
				numberOfTrips: options.numberOfTrips,
				comparisonOfTrips: options.comparisonOfTrips,
				statuses: options.statuses,
				fromDB: options.fromDB,
				// Новые ключи добавляем только при наличии: RPC передаёт
				// undefined как есть, и старый gateway отвергает их Joi-схемой.
				...(options.dateRange ? { dateRange: options.dateRange } : {}),
				...(options.withTotal ? { withTotal: true } : {}),
			};

			const res = await this.request(
				(prpc) => prpc.theirsModel.customer.getAll(params),
				{ silent: false, error: true },
			);

			logger.info("[Client] getAll", res);

			return {
				items: res?.items?.map(this.fromResponse),
				pagination: res?.pagination,
				total: res?.total,
			};
		} catch (error) {
			logger.error("[Client] error getAll", error);
			return { items: [], pagination: { count: 0, offset: 0, limit: 0 } };
		}
	}

	static async getHistory(id: number) {
		const prpc = getPRPC();

		if (!prpc) return [];

		const result = await createRPCQuery(() =>
			prpc.theirsModel.customer.getHistory(id),
		);

		return result as unknown as Client.History;
	}

	static async getFeedback(id: number) {
		try {
			const prpc = getPRPC();

			if (!prpc)
				return { items: [], pagination: { offset: 0, count: 0 } };

			const result = await createRPCQuery(() =>
				prpc.theirsModel.feedback.customer.getAll({
					query: { customerId: id },
				}),
			);

			return result as {
				items: Client.CustomerFeedback[];
				pagination: { offset: number; count: number };
			};
		} catch (error) {
			logger.error("[Client] error getFeedback", error);
			return { items: [], pagination: { offset: 0, count: 0 } };
		}
	}

	static async store(object: Client.Model.New) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.customer.create(Client.toRequest(object)),
				{ silent: false, error: true },
			);

			logger.info("[Client] create", { object, res });
			if (!res?.id) {
				return null;
			}

			return this.fromResponse(res);
		} catch (error) {
			logger.error("[Client] error create", error);
			return null;
		}
	}

	static async update(object: Client.Model.Modified) {
		try {
			const res = await this.request(
				(prpc) =>
					prpc.theirsModel.customer.update(
						object.id,
						Client.toRequest(object),
					),
				{ silent: false, error: true },
			);

			logger.info("[Client] update", { object, res });
			if (!res?.id) return null;

			return this.fromResponse(res);
		} catch (error) {
			logger.error("[Client] error update", error);
			return null;
		}
	}

	static async updateStatus(object: {
		id: Client.Model["id"];
		status: Client.Model["status"];
	}) {
		const prpc = getPRPC();

		if (!prpc) return;

		await createRPCQuery(() =>
			prpc.theirsModel.customer.update(object.id, object),
		);
	}

	static async rating(object: {
		id: Client.Model["id"];
	}): Promise<number | null> {
		try {
			const res = await this.request(
				(prpc) => prpc.theirsModel.feedback.customer.rating(object.id),
				{ silent: false, error: true },
			);

			return res;
		} catch (error) {
			logger.error(error);
			return null;
		}
	}

	static async destroy(id: number[] | number) {
		const prpc = getPRPC();

		if (!prpc) return;

		if (Array.isArray(id))
			await Promise.all(id.map((id) => destroyOne(id)));
		else await destroyOne(id);
	}

	static async importFromFile(payload: {
		file: globalThis.File;
		companyId: number;
		defaultTaxiServiceId: number;
		paymentComment?: string;
		onUploadProgress?: (loaded: number, total: number) => void;
	}): Promise<Client.ImportFromFileResult | null> {
		const prpc = getPRPC();

		if (!prpc) {
			logger.error(
				"[Client] importFromFile: нет PRPC-сессии — upload не отправлялся",
			);
			throw new Error(
				"[Client] importFromFile: WebSocket-сессия не готова (PRPC отсутствует)",
			);
		}

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			importFromFile: (p: {
				companyId: number;
				defaultTaxiServiceId: number;
				paymentComment?: string;
			}) => Promise<Client.ImportFromFileWebStream>;
		};

		const uploadStream = (await createRPCQuery(
			() =>
				customer.importFromFile({
					companyId: payload.companyId,
					defaultTaxiServiceId: payload.defaultTaxiServiceId,
					paymentComment: payload.paymentComment,
				}),
			{ silent: false },
		)) as Client.ImportFromFileWebStream;

		const reader = payload.file.stream().getReader();
		const decoder = new TextDecoder("utf-8");
		const totalBytes = payload.file.size;
		let loadedBytes = 0;

		payload.onUploadProgress?.(0, totalBytes);

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;

			if (value?.length) {
				loadedBytes += value.byteLength;
				payload.onUploadProgress?.(
					Math.min(loadedBytes, totalBytes),
					totalBytes,
				);

				const piece = decoder.decode(value, { stream: true });
				if (piece) {
					const writeResult = await uploadStream.write(piece);
					if (!writeResult.success) throw writeResult.error;
				}
			}
		}

		const tail = decoder.decode();
		if (tail) {
			const writeResult = await uploadStream.write(tail);
			if (!writeResult.success) throw writeResult.error;
		}

		const endResult = await uploadStream.end();
		if (!endResult.success) throw endResult.error;

		return endResult.data ?? null;
	}

	private static readonly IMPORT_CHUNK_SIZE = 2000;

	private static readonly IMPORT_MAX_CHUNK_RETRIES = 3;

	private static readonly IMPORT_RETRY_BACKOFF_MS = 2000;

	private static readonly IMPORT_MAX_ITEMS = 200_000;

	private static async withImportChunkRetry<T>(
		action: () => Promise<T>,
	): Promise<T> {
		let lastError: unknown;
		for (
			let attempt = 0;
			attempt < Client.IMPORT_MAX_CHUNK_RETRIES;
			attempt += 1
		) {
			try {
				return await action();
			} catch (error) {
				lastError = error;
				if (attempt < Client.IMPORT_MAX_CHUNK_RETRIES - 1) {
					await new Promise<void>((resolve) => {
						setTimeout(
							resolve,
							Client.IMPORT_RETRY_BACKOFF_MS * (attempt + 1),
						);
					});
				}
			}
		}
		throw lastError;
	}

	static async importFromFileChunked(payload: {
		file: globalThis.File;
		companyId: number;
		defaultTaxiServiceId: number;
		paymentComment?: string;
		onEnqueueProgress?: (enqueued: number, total: number) => void;
	}): Promise<Client.ImportFromFileResult | null> {
		const { parseLegacyClientImportAsync } = await import(
			"../../pages/MainPage/pages/Customers/tabs/Clients/utils/parseLegacyClientImport"
		);

		const running = await Client.getImportProgress();
		if (running?.status === "running") {
			throw new Error("Customer import is already running");
		}

		const text = await payload.file.text();
		let parsed: unknown;
		try {
			parsed = JSON.parse(text.replace(/^\uFEFF/, ""));
		} catch {
			throw new SyntaxError("Failed to parse JSON file");
		}

		const body = (parsed as { table?: { body?: unknown[] } })?.table?.body;
		if (Array.isArray(body) && body.length > Client.IMPORT_MAX_ITEMS) {
			throw new Error(`Too many rows (${body.length})`);
		}

		const { items, skippedCounterparties, skippedBrokenRows } =
			await parseLegacyClientImportAsync(parsed, {
				yieldEveryRows: 500,
			});

		if (!items.length) {
			throw new Error("No customers to import in file");
		}

		const total = items.length;
		const paymentComment =
			(payload.paymentComment || "").trim() || "Перенос с импорта";
		const chunkSize = Client.IMPORT_CHUNK_SIZE;

		payload.onEnqueueProgress?.(0, total);

		const failEnqueue = async (message: string) => {
			await Client.failImport(message).catch(() => undefined);
		};

		try {
			const firstBatch = items.slice(0, chunkSize);
			await Client.withImportChunkRetry(() =>
				Client.startImport({
					items: firstBatch,
					totalCustomers: total,
					companyId: payload.companyId,
					defaultTaxiServiceId: payload.defaultTaxiServiceId,
					paymentComment,
					isLast: total <= chunkSize,
				}),
			);
			payload.onEnqueueProgress?.(firstBatch.length, total);

			for (let offset = chunkSize; offset < total; offset += chunkSize) {
				const batch = items.slice(offset, offset + chunkSize);
				const isLast = offset + chunkSize >= total;
				await Client.withImportChunkRetry(() =>
					Client.appendImportChunk({ items: batch, isLast }),
				);
				payload.onEnqueueProgress?.(
					Math.min(offset + batch.length, total),
					total,
				);
			}
		} catch (error: unknown) {
			const message =
				error instanceof Error ? error.message : String(error);
			await failEnqueue(`Ошибка загрузки чанка на сервер: ${message}`);
			throw error;
		}

		const progress = await Client.getImportProgress();
		if (!progress) {
			return null;
		}

		return {
			progress,
			skippedCounterparties,
			skippedBrokenRows,
		};
	}

	static async startImport(payload: {
		items?: Client.ImportItem[];
		totalCustomers: number;
		companyId: number;
		defaultTaxiServiceId: number;
		paymentComment?: string;
		isLast?: boolean;
	}) {
		const prpc = getPRPC();

		if (!prpc) {
			logger.error(
				"[Client] startImport: нет PRPC-сессии — customer.startImport не отправлялся",
			);
			throw new Error(
				"[Client] startImport: WebSocket-сессия не готова (PRPC отсутствует)",
			);
		}

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			startImport: (p: {
				items?: Client.ImportItem[];
				totalCustomers: number;
				companyId: number;
				defaultTaxiServiceId: number;
				paymentComment?: string;
				isLast?: boolean;
			}) => Promise<Client.ImportProgress | null>;
		};

		return createRPCQuery(() => customer.startImport(payload), {
			silent: false,
		});
	}

	static async appendImportChunk(payload: {
		items: Client.ImportItem[];
		isLast?: boolean;
	}) {
		const prpc = getPRPC();

		if (!prpc) {
			logger.error(
				"[Client] appendImportChunk: нет PRPC-сессии — chunk не отправлялся",
			);
			throw new Error(
				"[Client] appendImportChunk: WebSocket-сессия не готова (PRPC отсутствует)",
			);
		}

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			appendImportChunk: (p: {
				items: Client.ImportItem[];
				isLast?: boolean;
			}) => Promise<Client.ImportProgress | null>;
		};

		return createRPCQuery(() => customer.appendImportChunk(payload), {
			silent: false,
		});
	}

	static async cancelImport() {
		const prpc = getPRPC();

		if (!prpc) return null;

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			cancelImport: () => Promise<Client.ImportProgress | null>;
		};

		return createRPCQuery(() => customer.cancelImport(), {
			silent: false,
		});
	}

	static async failImport(message: string) {
		const prpc = getPRPC();

		if (!prpc) return null;

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			failImport: (p: {
				message: string;
			}) => Promise<Client.ImportProgress | null>;
		};

		return createRPCQuery(() => customer.failImport({ message }), {
			silent: false,
		});
	}

	static async getImportProgress() {
		const prpc = getPRPC();

		if (!prpc) return null;

		const customer = prpc.theirsModel
			.customer as typeof prpc.theirsModel.customer & {
			getImportProgress: () => Promise<Client.ImportProgress | null>;
		};

		const res = await createRPCQuery(() => customer.getImportProgress(), {
			silent: true,
		});

		return res;
	}

	static Global = {
		async index(options: Client.SubscribeOptions) {
			const prpc = getPRPC();
			if (!prpc) return null;
			if (!prpc.theirsModel?.customer?.getAll) return null;

			const result = await createRPCQuery(() =>
				prpc.theirsModel.customer.getAll({
					limit: options.limit,
					offset: options.offset,
					order: options.order,
					query: options.query,
				}),
			);

			const cache = (result.items as any[]).map(Client.fromResponse);

			return {
				cache,
				offset: 0,
				limit: cache.length,
				total: cache.length,
				deprecated: false,
			};
		},
	};

	/**
	 * `onModelUpdate` — only live changes of a customer already known to the
	 * subscription (raw `UPDATE` with a body). Hydration (`INIT`/`ADD`/
	 * `REGENERATE`) never calls it: those snapshots come from the Redis cache
	 * and may lag behind the database (clients table, live balance).
	 */
	public static async subscribe(
		options: Client.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<Client.Model>,
		onModelUpdate?: (model: Client.Model) => void,
	): Promise<Subscription<Client.SubscribeOptions> | null> {
		let disposed = false;
		// SubscriptionPool.onReconnect calls the same request function: the
		// server must get the last options (ids grow as pages load).
		let currentOptions = options;

		const modelEventConstructor = new ModelEventConstructor({
			hydrateWindowSize:
				typeof options.limit === "number" && options.limit >= 0
					? options.limit
					: 30,
			onUpdate: (state) => {
				if (disposed) return;
				logger.info("[Client] subscribe", {
					count: state.models.length,
				});
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.customer.subscribe({
					params: this.optionsToRequest(currentOptions),
					ping: () => true,
					onEvent: (events) => {
						if (disposed) return;
						logger.info(
							"[Client] subscribe events",
							events?.length ?? 0,
						);
						if (onModelUpdate) {
							(events ?? []).forEach((event) => {
								if (
									event?.action === ModelEventAction.UPDATE &&
									event.data
								) {
									onModelUpdate(
										this.fromResponse(event.data as any),
									);
								}
							});
						}
						modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "Client.subscribe" },
		);

		return {
			unsubscribe: () => {
				disposed = true;
				return subscription.unsubscribe();
			},
			update: (nextOptions: Client.SubscribeOptions) => {
				currentOptions = nextOptions;
				modelEventConstructor.setHydrateWindowSize(nextOptions.limit);
				return subscription.update(this.optionsToRequest(nextOptions));
			},
		} as Subscription<Client.SubscribeOptions>;
	}

	private static optionsToRequest(options: Client.SubscribeOptions) {
		const retval = {
			ids: options.ids,
			query: options.query,
			offset: options.offset,
			limit: options.limit,
			order: options.order,
			subscribeType: options.subscribeType,
			lang: options.language,
			defaultTaxiServiceIds: options.defaultTaxiServiceIds,
			companyIds: options.companyIds,
			isApp: options.isApp,
			numberOfTrips: options.numberOfTrips,
			comparisonOfTrips: options.comparisonOfTrips,
			statuses: options.statuses,
		};

		return retval;
	}
}

//* CustomerComparisonEnum

export enum CustomerComparisonEnum {
	LESS_THAN = "less_than",
	EQUAL = "equal",
	GREATER_THAN = "greater_than",
	LESS_THAN_AND_EQUAL = "less_than_and_equal",
	GREATER_THAN_AND_EQUAL = "greater_than_and_equal",
} // "<" | "=" | ">" | "<=" | ">="

export const CUSTOMER_COMPARISON_TYPE: string[] = [
	CustomerComparisonEnum.LESS_THAN,
	CustomerComparisonEnum.EQUAL,
	CustomerComparisonEnum.GREATER_THAN,
	CustomerComparisonEnum.LESS_THAN_AND_EQUAL,
	CustomerComparisonEnum.GREATER_THAN_AND_EQUAL,
];

export const CustomerComparison = {
	LESS_THAN: "less_than",
	EQUAL: "equal",
	GREATER_THAN: "greater_than",
	LESS_THAN_AND_EQUAL: "less_than_and_equal",
	GREATER_THAN_AND_EQUAL: "greater_than_and_equal",
} as const;

export type TypeCustomerComparison = typeof CustomerComparison;
export type ValueCustomerComparison =
	(typeof CustomerComparison)[keyof typeof CustomerComparison];

//* CustomerSubscribeTypeEnum

export enum CustomerSubscribeTypeEnum {
	CUSTOMER_PAGE = "customer_page",
	CUSTOMER_MODAL = "customer_modal",
	CUSTOMER_MULTISELECT_MODAL = "customer_multiselect_modal",
	MAIN_ORDER_PAGE = "main_order_page",
}

export const CUSTOMER_SUBSCRIBE_TYPE: string[] = [
	CustomerSubscribeTypeEnum.CUSTOMER_PAGE,
	CustomerSubscribeTypeEnum.CUSTOMER_MODAL,
	CustomerSubscribeTypeEnum.CUSTOMER_MULTISELECT_MODAL,
	CustomerSubscribeTypeEnum.MAIN_ORDER_PAGE,
];

export const CustomerSubscribeType = {
	CUSTOMER_PAGE: "customer_page",
	CUSTOMER_MODAL: "customer_modal",
	CUSTOMER_MULTISELECT_MODAL: "customer_multiselect_modal",
	MAIN_ORDER_PAGE: "main_order_page",
} as const;
export type TypeCustomerSubscribeType = typeof CustomerSubscribeType;
export type ValueCustomerSubscribeType =
	(typeof CustomerSubscribeType)[keyof typeof CustomerSubscribeType];

//

namespace Client {
	export enum Status {
		ACTIVE = "active",
		NO_ACTIVE = "not_active",
		PHONE = "phone",
		BLOCKED = "blocked",
		WARNING = "warning",
	}

	export namespace Status {
		export type Closed = "blocked" | "dismissed";
	}

	export type History = History.Item[];

	export namespace History {
		export interface Item {
			id: number;

			user: null;

			action: "create" | "update" | "delete";
			comment: string;
			changes: Entry[];

			timestamp: number;
			version: number;
		}

		export namespace Entry {
			export interface AnyChange<
				Field extends string,
				Value,
				Type extends "simple" | "array",
			> {
				previous?: Value;
				actual?: Value;
				field: Field;
				type: Type extends "simple"
					? "update"
					:
							| "array_item_add"
							| "array_item_remove"
							| "array_item_update";
			}

			// Personal data
			export type NameChange = AnyChange<"name", string, "simple">;
			export type SurnameChange = AnyChange<"surname", string, "simple">;
			export type FatherNameChange = AnyChange<
				"fatherName",
				string,
				"simple"
			>;
			export type BirthdayChange = AnyChange<"birthday", Date, "simple">;
			export type GenderChange = AnyChange<"gender", Gender, "simple">;

			export type EmailsChange = AnyChange<
				"emails",
				Person.Model.Email[],
				"array"
			>;
			export type PhonesChange = AnyChange<
				"phones",
				Person.Model.Phone[],
				"array"
			>;

			// Additional data
			// type ClientGroupChange = AnyChange<"clientGroup", Record<Language,string>, "simple">;
			// type MainLoyaltyProgramChange = AnyChange<"mainLoyaltyProgram", Record<Language,string>, "simple">;
			// type AdditionalLoyaltyProgramChange = AnyChange<"additionalLoyaltyProgram", Record<Language,string>, "simple">;
			// type BonusCardChange = AnyChange<"bonusCard", Record<Language,string>, "simple">;
			// type TariffChange = AnyChange<"tariff", Record<Language,string>, "simple">;
			export type CarClassChange = AnyChange<
				"carClass",
				Record<Language, string>,
				"simple"
			>;

			// Other
			export type StatusChange = AnyChange<
				"status",
				Client.Status,
				"simple"
			>;
			// type CompanyChange = AnyChange<"company", any, "simple">;
			// type TaxiServiceChange = AnyChange<"taxiService", any, "simple">;

			// Options
			export type SMSNotificationsChange = AnyChange<
				"smsNotifications",
				boolean,
				"simple"
			>;
		}

		export type Entry =
			| Entry.NameChange
			| Entry.SurnameChange
			| Entry.FatherNameChange
			| Entry.BirthdayChange
			| Entry.GenderChange
			| Entry.EmailsChange
			| Entry.PhonesChange
			| Entry.CarClassChange
			| Entry.StatusChange
			| Entry.SMSNotificationsChange;
	}

	export enum Group {
		main,
		additional,
	}

	export interface AdditionalFields {
		defaultAddress?: string;

		additionalRideCounts?: {
			closedRideCount: number;
			successRideCount: number;
			canceledRideCount: number;
		};
	}

	export interface Company {
		id: number;
		name: Record<Language, string>;
		login: string;

		createdAt: Date;
		updatedAt: Date;
		deletedAt: Date | null;
	}

	export interface ExecutorRelation {
		id: number;
		notes: string;
		status: string;

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	export interface ForbiddenExecutors extends ExecutorRelation {
		status: "forbidden";
		forbiddenExecutors: unknown;
	}
	export interface UndesirableExecutors extends ExecutorRelation {
		status: "undesirable";
		undesirableExecutors: unknown;
	}
	export interface PriorityExecutors extends ExecutorRelation {
		status: "priority";
		priorityExecutors: unknown;
	}

	export interface CarClass {
		id: number;
		name: Record<Language, string>;
		active: boolean;
		default: boolean;
		position: string; // '2023-06-12T14:01:59.091Z',

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	export interface Model {
		id: number;

		additionalPlan?: DiscountPlan | null;
		mainPlan?: DiscountPlan | null;

		mainRate?: CustomerRate.Model;
		additionalRate?: CustomerRate.Model;
		rideCount: number;
		counters: {
			total: number;
			success: number;
			cancelled: number;
			failed: number;
		};

		customerGroup: ClientGroup.Model;
		customerGroupId: number | undefined;

		person: Person.Model;
		gender: Nullable<Gender>;

		paymentAccounts: PaymentAccount[];

		carClassId?: number;
		carClassText?: string;

		status: {
			level: Status;
			description: string;
		};
		companyId: number;
		defaultTaxiServiceId?: number;
		latestTaxiServiceId?: number;

		notes: string;
		orderNotes?: string;
		executorNotes?: string;

		smsNotifications: boolean;
		login?: string;
		hasPassword: boolean;
		password?: string;

		additionalFields: AdditionalFields;

		customerToForbiddenExecutors: ForbiddenExecutors[];
		customerToUndesirableExecutors: UndesirableExecutors[];
		customerToPriorityExecutors: PriorityExecutors[];

		personalFiles: File.Model[];
		otherFiles: File.Model[];
		downloadedFiles: File.Model[];

		passengers: any[];
		customerToPaymentTypes: any[];
		customerToCustomerCompanies: any[];

		isApp: boolean;
		isAllowedApp: boolean;
		isAllowedCabinet: boolean;

		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;
	}

	export interface SubscribeOptions
		extends ServiceSubscribeOptionsBase<Client.Model> {
		/** Only these customers (the rows a table has loaded). */
		ids?: number[];
		language?: Language;
		statuses?: Client.Status[];
		subscribeType?: ValueCustomerSubscribeType;
		isApp?: boolean;
		defaultTaxiServiceIds?: number[];
		companyIds?: number[];

		isCounterparty?: boolean;
		isVerify?: boolean;
		hasCard?: boolean;
		mainDiscountPlanIds?: number[];
		additionalDiscountPlanIds?: number[];

		numberOfTrips?: number;
		comparisonOfTrips?: ValueCustomerComparison;
		/** Период создания клиента (customer.createdAt). */
		dateRange?: { from: Date; to: Date };
	}

	export interface SharedOptions {
		deprecate?: boolean;
	}

	export interface ImportItem {
		phone: string;
		name?: string;
		surname?: string;
		fatherName?: string;
		notes?: string;
		ridesTotal: number;
		bonusBalance?: number;
		additionalFields?: Record<string, string>;
	}

	export interface ImportProgress {
		status: "running" | "completed" | "failed" | "cancelled";
		totalCustomers: number;
		processedCustomers: number;
		createdCount: number;
		mergedCount: number;
		alreadyImportedCount: number;
		failedCount: number;
		skippedCounterparties?: number;
		skippedBrokenRows?: number;
		errors: { index: number; message: string }[];
		startedAt: string;
		updatedAt: string;
		finishedAt?: string;
	}

	export interface ImportFromFileResult {
		progress: ImportProgress;
		skippedCounterparties: number;
		skippedBrokenRows: number;
	}

	export interface ImportFromFileWebStream {
		write: (
			data: string | Buffer | Array<number>,
		) => Promise<{ success: true } | { success: false; error: Error }>;
		end: () => Promise<
			| { success: true; data: ImportFromFileResult }
			| { success: false; error: Error }
		>;
	}

	export namespace Model {
		export interface CreateParams {
			companyId: number | undefined; // required for creation
			mainDiscountPlanId?: number;
			additionalDiscountPlanId?: number;

			name?: string;
			surname?: string;
			fatherName?: string;
			birthday?: Date;
			city?: string;
			country?: string;
			address?: string;

			phones: Pick<Person.Model.Phone, "number" | "group">[];
			emails: Pick<Person.Model.Email, "value" | "group">[];

			login?: string;
			password?: string;
			gender?: Nullable<string>;
			status?: {
				level: Status;
				description: string;
			};

			notes?: string;
			orderNotes?: string;
			executorNotes?: string;

			carClassId?: number;
			defaultTaxiServiceId?: number;

			// avatarFileId?: number;
			// otherFileIds?: number[];

			forbiddenExecutors?: ForbiddenExecutors[];
			undesirableExecutors?: UndesirableExecutors[];
			priorityExecutors?: PriorityExecutors[];

			smsNotifications?: boolean;

			customerGroupId?: number;

			isAllowedApp?: boolean;
			isAllowedCabinet?: boolean;

			additionalFields?: AdditionalFields;

			personalFileIds?: number[];
			otherFileIds?: number[];

			paymentTransactions?: Partial<PaymentTransactions>;
		}

		export type UpdateParams = Partial<CreateParams>;

		export type NonEditablePropertyNames =
			| "id"
			| "createdAt"
			| "updatedAt"
			| "deletedAt";

		export type ModifiedPropertyNames = "person";

		export type PartialNewPropertyNames =
			| "gender"
			| "status"
			| "notes"
			| "company"
			| "carClass"
			| "avatarFile"
			| "otherFiles"
			| "customerToForbiddenExecutors"
			| "customerToUndesirableExecutors"
			| "customerToPriorityExecutors"
			| "isBlockedApp"
			| "isBlockedCabinet"
			| "smsNotifications"
			| "additionalFields";

		export type RequiredNewProperties = Required<Pick<Model, "companyId">>;
		export type PartialNewProperties = Partial<
			Pick<
				Model,
				| "companyId"
				| "carClassId"
				| "defaultTaxiServiceId"
				| "customerGroupId"
				//
				// | "avatarFile"
				// | "otherFiles"
				//
				| "customerToForbiddenExecutors"
				| "customerToUndesirableExecutors"
				| "customerToPriorityExecutors"
				//
				| "status"
				| "gender"
				| "notes"
				| "orderNotes"
				| "executorNotes"
				| "login"

				//
				| "smsNotifications"
				//
				| "isAllowedApp"
				| "isAllowedCabinet"
				//
				| "additionalFields"
			>
		>;

		export type New = RequiredNewProperties &
			PartialNewProperties & {
				mainRateId?: number;
				additionalRateId?: number;

				password?: string;
				person: Person.Model.New;

				personalFileIds: number[];
				otherFileIds: number[];

				paymentTransactions: PaymentTransactions;
			};

		type PaymentTransactions = {
			main: { amount: number; description?: string }[];
			bonus: { amount: number; description?: string }[];
		};

		export type Modified = Pick<Model, "id"> &
			Partial<
				Omit<
					Model,
					NonEditablePropertyNames | ModifiedPropertyNames
				> & {
					mainRateId?: number;
					additionalRateId?: number;

					password?: string;
					person: Person.Model.Modified;

					personalFileIds: number[];
					otherFileIds: number[];
				}
			>;
	}

	export interface CustomerFeedback {
		id: number;
		v: number;
		createdAt: string;
		updatedAt: string;
		deletedAt: string | null;

		rating: number;
		comment: string | null;

		customer: {
			id: number;
			[key: string]: unknown;
		};

		order: {
			id: number;
			[key: string]: unknown;
		};

		executor: {
			id: number;
			[key: string]: unknown;
		};
	}
}

export default Client;
